import { useCallback, useEffect, useRef, useState } from "react";
import "./MyTurn.css";
import { Button } from "./Button";
import { Alert, EmptyState, Loading } from "./Card";
import { Icon } from "./Icon";
import { StatusPill } from "./Status";
import { errText, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 내 차례 — 상단 바의 서랍 단추(t-in-port-shell) ══
   b3x-pr 의 「내 차례」(승인 대기 · 내 담당)를 b3x-in 에 옮긴 것이다. b3x-in 에는 승인 관문(gate)이 없어서
   「승인 대기」 자리를 **사람이 답해야 하는 질문**이 받는다 — 세션이 ask_human 으로 남기고 멈춘 것이라, 사람이
   안 보면 그 이슈는 영영 안 움직인다. 나머지 하나는 **내가 맡아 둔 이슈**(`assigneeKind === "ticket"` 이고 담당이
   나)다. 막힘 · 세션 끝남 같은 「일어난 일」은 종(알람)의 몫이라 여기 다시 적지 않는다.
   판은 공통 `.b-popover` + `.b-popover-item` 이고, 줄을 누르면 보드에서 그 이슈 상세가 열린다. */

export type MyTurnRows = { questions: Issue[]; mine: Issue[] };
type State = { kind: "loading" } | { kind: "error"; msg: string } | { kind: "ok"; rows: MyTurnRows };

const CLOSED = new Set(["done", "cancelled"]);

/** 목록에서 내 차례만 고른다 — 판정이 아니라 **사실 둘**(열린 질문이 있다 · 담당이 나다)로 거른다. */
export function myTurnOf(list: Issue[], me: string): MyTurnRows {
  const open = list.filter((t) => !CLOSED.has(t.status));
  return {
    questions: open.filter((t) => (t.openQuestions?.length ?? 0) > 0),
    mine: open.filter((t) => t.assigneeKind === "ticket" && t.assignee === me),
  };
}

const total = (r: MyTurnRows) => r.questions.length + r.mine.length;
const hrefOf = (t: Issue) => `#/board?open=${encodeURIComponent(t.id)}`;

function Row({ t, note }: { t: Issue; note?: string }) {
  return (
    <a className="b-popover-item myturn__item" href={hrefOf(t)}>
      <span className="myturn__top">
        <StatusPill status={t.status} working={t.openSession !== null} />
        <span className="myturn__title">{t.title}</span>
      </span>
      <span className="myturn__meta">
        {t.id}
        {note ? ` · ${note}` : null}
      </span>
    </a>
  );
}

/** 서랍 몸통 — 패턴 페이지가 데이터 없이 상태마다 세운다. */
export function MyTurnPanel({ state, onRetry, inline }: { state: State; onRetry?: () => void; inline?: boolean }) {
  return (
    <div className={`b-popover myturn${inline ? " myturn--inline" : ""}`} role="dialog" aria-label={tx("myturn.title")}>
      <div className="b-popover-head">
        <span>{tx("myturn.title")}</span>
        <span className="myturn__count">{state.kind === "ok" ? tx("myturn.count", { n: total(state.rows) }) : null}</span>
      </div>
      {state.kind === "loading" ? (
        <div className="myturn__state">
          <Loading text={tx("common.state.loading")} />
        </div>
      ) : null}
      {state.kind === "error" ? (
        <div className="myturn__state">
          <Alert tone="error">{tx("myturn.loadFailed", { err: state.msg })}</Alert>
          {onRetry ? (
            <Button size="sm" onClick={onRetry}>
              {tx("common.action.retry")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {state.kind === "ok" && total(state.rows) === 0 ? (
        <EmptyState icon="check" title={tx("myturn.empty")} desc={tx("myturn.emptyDesc")} />
      ) : null}
      {state.kind === "ok" && total(state.rows) > 0 ? (
        <div className="myturn__list">
          {state.rows.questions.length ? (
            <>
              <span className="b-sidenav-group myturn__group">{tx("myturn.questions", { n: state.rows.questions.length })}</span>
              {state.rows.questions.map((t) => (
                <Row key={t.id} t={t} note={t.openQuestions?.[0]?.prompt} />
              ))}
            </>
          ) : null}
          {state.rows.mine.length ? (
            <>
              <span className="b-sidenav-group myturn__group">{tx("myturn.mine", { n: state.rows.mine.length })}</span>
              {state.rows.mine.map((t) => (
                <Row key={t.id} t={t} />
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 단추 — 수는 **열었을 때와 처음 뜰 때** 다시 센다(타이머 없이). 못 셌으면 수를 안 적는다(0 이 아니다). */
export function MyTurnButton({ me, forceOpen }: { me: string; forceOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(forceOpen));
  const [state, setState] = useState<State>({ kind: "loading" });
  const wrap = useRef<HTMLDivElement>(null);

  const read = useCallback(() => {
    setState({ kind: "loading" });
    tasksApi
      .list()
      .then((list) => setState({ kind: "ok", rows: myTurnOf(list, me) }))
      .catch((e) => setState({ kind: "error", msg: errText(e) }));
  }, [me]);

  useEffect(read, [read]);
  useEffect(() => {
    if (!open || forceOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // 줄을 눌러 보드로 가면 서랍을 닫는다 — 열린 채로 상세를 가리지 않게.
    const onHash = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    addEventListener("hashchange", onHash);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      removeEventListener("hashchange", onHash);
    };
  }, [open, forceOpen]);

  const n = state.kind === "ok" ? total(state.rows) : null;
  const label = n ? tx("myturn.buttonN", { n }) : tx("myturn.title");
  return (
    <div className="b-menu-wrap" ref={wrap}>
      <button
        type="button"
        className={`b-btn b-btn-ghost myturn__btn${open ? " is-on" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => {
          if (!open) read();
          setOpen((v) => !v);
        }}
      >
        <Icon name="inbox" />
        {n ? <span className="b-badge b-badge-primary myturn__badge">{n}</span> : null}
      </button>
      {open ? <MyTurnPanel state={state} onRetry={read} /> : null}
    </div>
  );
}

export type { State as MyTurnState };
